import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
  useTheme,
  Grid,
  AlertTitle,
} from '@mui/material';
import { DemoRequestFormData, DemoProvisioningResult } from '../../types';
import { DemoSuccessModal } from '../DemoSuccessModal';
import { trpc } from '../../../../utils/trpc/client';

/**
 * Maximum length for error messages considered user-friendly enough to display directly
 */
const MAX_USER_FRIENDLY_ERROR_LENGTH = 200;

/**
 * Error message mappings for user-friendly display
 */
const ERROR_MESSAGES: Record<string, string> = {
  rate_limit:
    'A demo was already requested with this email recently. Please try again later or use the existing credentials.',
  email_exists:
    'This email already has an active demo. Please log in with your existing credentials.',
  server_error: 'Something went wrong on our end. Please try again later or contact support.',
  network_error: 'Unable to connect. Please check your internet connection and try again.',
};

/**
 * Parse error message from tRPC error
 */
function parseErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes('rate limit') || message.includes('already requested')) {
      return ERROR_MESSAGES.rate_limit;
    }
    if (message.includes('already has an active demo')) {
      return ERROR_MESSAGES.email_exists;
    }
    if (message.includes('network') || message.includes('fetch')) {
      return ERROR_MESSAGES.network_error;
    }

    // Return the actual error message if it's user-friendly
    if (
      error.message.length < MAX_USER_FRIENDLY_ERROR_LENGTH &&
      !error.message.includes('Error:')
    ) {
      return error.message;
    }
  }

  return ERROR_MESSAGES.server_error;
}

/**
 * Demo request form component with tRPC integration
 *
 * Features:
 * - Form validation matching backend requirements
 * - Loading states during submission
 * - Error handling with user-friendly messages
 * - Success modal with credentials display
 */
export const DemoRequestForm: React.FC = () => {
  const theme = useTheme();
  const [demoResult, setDemoResult] = useState<DemoProvisioningResult | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DemoRequestFormData>();

  // tRPC mutation for requesting demo
  const requestDemoMutation = trpc.demo.requestDemo.useMutation({
    onSuccess: (data) => {
      // Map backend response to frontend type
      const result: DemoProvisioningResult = {
        success: data.success,
        message: data.message,
        demoId: data.demoId,
        accessUrl: data.accessUrl,
        email: data.email,
        temporaryPassword: data.temporaryPassword,
        expiresAt: data.expiresAt,
        durationHours: data.durationHours,
      };

      setDemoResult(result);
      setShowSuccessModal(true);
      setErrorMessage(null);
      reset();
    },
    onError: (error) => {
      setErrorMessage(parseErrorMessage(error));
    },
  });

  const onSubmit = async (data: DemoRequestFormData) => {
    setErrorMessage(null);

    requestDemoMutation.mutate({
      email: data.email.trim(),
      name: data.name.trim(),
      organizationName: data.organizationName?.trim() || undefined,
      message: data.message?.trim() || undefined,
    });
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const isSubmitting = requestDemoMutation.isPending;

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Error Alert */}
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage(null)}>
            <AlertTitle>Request Failed</AlertTitle>
            {errorMessage}
          </Alert>
        )}

        <Grid container spacing={2.5}>
          {/* Name - Required */}
          <Grid item xs={12} sm={6}>
            <TextField
              {...register('name', {
                required: 'Name is required',
                minLength: {
                  value: 2,
                  message: 'Name must be at least 2 characters',
                },
                maxLength: {
                  value: 100,
                  message: 'Name must be at most 100 characters',
                },
              })}
              label="Full Name"
              fullWidth
              required
              disabled={isSubmitting}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </Grid>

          {/* Email - Required */}
          <Grid item xs={12} sm={6}>
            <TextField
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              label="Email Address"
              type="email"
              fullWidth
              required
              disabled={isSubmitting}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          </Grid>

          {/* Organization - Optional */}
          <Grid item xs={12}>
            <TextField
              {...register('organizationName', {
                minLength: {
                  value: 2,
                  message: 'Organization name must be at least 2 characters',
                },
                maxLength: {
                  value: 255,
                  message: 'Organization name must be at most 255 characters',
                },
              })}
              label="University / Organization (Optional)"
              fullWidth
              disabled={isSubmitting}
              error={!!errors.organizationName}
              helperText={errors.organizationName?.message}
              placeholder="e.g., University of Oslo, NTNU"
            />
          </Grid>

          {/* Message - Optional */}
          <Grid item xs={12}>
            <TextField
              {...register('message', {
                maxLength: {
                  value: 1000,
                  message: 'Message must be at most 1000 characters',
                },
              })}
              label="Additional Information (Optional)"
              multiline
              rows={3}
              fullWidth
              disabled={isSubmitting}
              error={!!errors.message}
              helperText={errors.message?.message}
              placeholder="Tell us about your specific needs or questions"
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isSubmitting}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: 'white',
                py: 1.5,
                fontWeight: 600,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                },
                '&:disabled': {
                  background: theme.palette.action.disabledBackground,
                },
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} color="inherit" />
                  Creating your demo...
                </Box>
              ) : (
                'Get Instant Access'
              )}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Success Modal */}
      <DemoSuccessModal
        open={showSuccessModal}
        onClose={handleCloseSuccessModal}
        demoResult={demoResult}
      />
    </>
  );
};
